import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
// UI
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import { useToast } from "@/components/ui/use-toast";
import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Icons
import { PencilLine, ArrowLeft } from "lucide-react";

// Services and Utils
import { getEquities } from "@/services/finance";
import type { EquityApiResponse } from "@/types/finance";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/owner-equity/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const [unit, setUnit] = React.useState("1");
  const { company_financial_report } = useAccessPermission();

  const { toast } = useToast();
  React.useEffect(() => {
    if (!year) {
      navigate({ to: "/report/finance/company/owner-equity/add" });
    }
  }, [year, navigate]);
  const { data, isLoading, error } = useQuery<EquityApiResponse>({
    queryKey: ["equities", companyId, year],
    queryFn: () => getEquities(companyId as string, year),
    enabled: Boolean(companyId && year),
  });
  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({ to: "/report/finance/company/owner-equity/add" });
    }
  }, [data, navigate, isLoading]);
  if (!companyId || !year) {
    return <div>Missing required data</div>;
  }

  if (isLoading) {
    return <DinamixSkeletonTable />;
  }

  if (error) return <div>Error loading data</div>;

  const equitiesData = data?.data;
  console.log("equitiesData", equitiesData);

  const initialNotCurrentYearEquity = equitiesData?.equityDetails?.filter(
    (item) => item?.isCurrentYear === false
  );

  const initialCurrentYearEquity = equitiesData?.equityDetails?.filter(
    (item) => item?.isCurrentYear === true
  );

  // Function

  const formatNumberWithCommas = (value: number) => {
    // Convert value to a string and remove any existing commas
    const stringValue = String(value || "").replace(/,/g, "");

    // Check if the value is negative
    const isNegative = stringValue.startsWith("-");

    // Remove any non-numeric characters (such as parentheses) and handle negative values
    const numericValue = isNegative
      ? stringValue.slice(1)
      : stringValue.replace(/[()]/g, "");

    // Convert the numeric value to a number
    const numericAmount = Number(numericValue);

    // If the value is invalid (NaN), return the original string value
    if (Number.isNaN(numericAmount)) {
      return value;
    }

    // Format the numeric value with commas
    const formattedValue = numericAmount.toLocaleString();

    // If it's negative, wrap it in parentheses
    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  function parseParenthesisNumber(strValue: string | number) {
    if (!strValue) return 0;
    const stringValue = String(strValue).replace(/,/g, ""); // remove commas

    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

    const parsed = Number(numericValue) || 0;
    return isNegative ? -parsed : parsed;
  }

  function computeSumForField(data: any[], fieldName: string) {
    const sum = data?.reduce((acc, row) => {
      return acc + parseParenthesisNumber(row[fieldName]);
    }, 0);
    return sum?.toLocaleString();
  }

  function computeRowTotal(row: any) {
    const cap = parseParenthesisNumber(row.registeredCapital);
    const str = parseParenthesisNumber(row.storage);
    const exp = parseParenthesisNumber(row.expansion);
    const acc = parseParenthesisNumber(row.accProfitLoss);
    const oth = parseParenthesisNumber(row.other);

    const total = cap + str + exp + acc + oth;
    return total.toLocaleString();
  }

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {year}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/owner-equity/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md  p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <div>
            {isLoading ? (
              <DinamixSkeletonTable rows={5} columns={3} />
            ) : (
              <Table className="mb-4">
                <TableHeader>
                  <TableRow className="[&>*]:border [&>*]:w-[180px] [&>*]:whitespace-nowrap  [&>*]:font-bold [&>*]:text-black">
                    <TableHead style={{ width: 220 }}>ເນຶ້ອໃນລາຍງານ</TableHead>
                    <TableHead>ທຶນຈົດທະບຽນ</TableHead>
                    <TableHead>ຄັງສະສົມຕາມລະບຽບການ</TableHead>
                    <TableHead>ຄັງຂະຫຍາຍການຜະລິດ</TableHead>
                    <TableHead>ກຳໄລ-ຂາດທຶນສະສົມ</TableHead>
                    <TableHead>ອື່ນ</TableHead>
                    <TableHead>ລວມທຶນ</TableHead>
                    <TableHead>ໝາຍເຫດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/*  */}
                  {initialNotCurrentYearEquity
                    ?.filter(
                      (equity) =>
                        !equity?.isCurrentYear &&
                        equity?.name?.startsWith("I. ຍອດເຫຼຶອ 1 ມັງກອນ")
                    )
                    ?.map((equity) => (
                      <TableRow
                        key={equity.id}
                        className="[&>*]:whitespace-nowrap bg-[#216C4C]/20"
                      >
                        <TableCell className="border">
                          {equity.name || "-"}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.registeredCapital / unit || "-"} */}
                          {formatNumberWithCommas(equity.registeredCapital)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.storage / unit || "-"} */}
                          {formatNumberWithCommas(equity.storage)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.expansion / unit || "-"} */}
                          {formatNumberWithCommas(equity.expansion)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.accProfitLoss)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.other)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {(equity.registeredCapital +
														equity.storage +
														equity.expansion +
														equity.accProfitLoss) /
														unit || "-"} */}

                          {formatNumberWithCommas(
                            (equity.registeredCapital +
                              equity.storage +
                              equity.expansion +
                              equity.accProfitLoss) /
                              unit
                          )}
                        </TableCell>
                        <TableCell className="border">
                          {equity.remark || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  {initialNotCurrentYearEquity
                    ?.filter(
                      (equity) =>
                        !equity?.isCurrentYear &&
                        !equity?.name?.startsWith("I. ຍອດເຫຼຶອ 1 ມັງກອນ")
                    )
                    ?.map((equity) => (
                      <TableRow
                        key={equity.id}
                        className="[&>*]:whitespace-nowrap "
                      >
                        <TableCell className="border">
                          {equity.name || "-"}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.registeredCapital / unit || "-"} */}
                          {formatNumberWithCommas(equity.registeredCapital)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.storage / unit || "-"} */}
                          {formatNumberWithCommas(equity.storage)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.expansion / unit || "-"} */}
                          {formatNumberWithCommas(equity.expansion)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.accProfitLoss)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.other)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {(equity.registeredCapital +
														equity.storage +
														equity.expansion +
														equity.accProfitLoss) /
														unit || "-"} */}

                          {formatNumberWithCommas(
                            (equity.registeredCapital +
                              equity.storage +
                              equity.expansion +
                              equity.accProfitLoss) /
                              unit
                          )}
                        </TableCell>
                        <TableCell className="border">
                          {equity.remark || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow className="bg-[#216C4C]/20">
                    <TableCell className="border">
                      II. ປັບປຸງຍອດຄືນໃຫມ່ 1 ມັງກອນ {year}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialNotCurrentYearEquity,
                          "registeredCapital"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialNotCurrentYearEquity,
                          "storage"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialNotCurrentYearEquity,
                          "expansion"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialNotCurrentYearEquity,
                          "accProfitLoss"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(initialNotCurrentYearEquity, "other")
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {(() => {
                        const total = [
                          "registeredCapital",
                          "storage",
                          "expansion",
                          "accProfitLoss",
                          "other",
                        ].reduce(
                          (acc, field) =>
                            acc +
                            parseParenthesisNumber(
                              initialNotCurrentYearEquity?.reduce(
                                (sum, row) =>
                                  sum + parseParenthesisNumber(row[field]),
                                0
                              )
                            ),
                          0
                        );
                        return formatNumberWithCommas(total);
                      })()}
                    </TableCell>
                    <TableCell className="border">-</TableCell>
                  </TableRow>
                  <TableRow className="bg-[#216C4C]/20">
                    <TableCell className="border">
                      III. ສ່ວນປ່ຽນແປງທຶນເຈົ້າຂອງ ໃນປີ {year}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialCurrentYearEquity,
                          "registeredCapital"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(initialCurrentYearEquity, "storage")
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialCurrentYearEquity,
                          "expansion"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(
                          initialCurrentYearEquity,
                          "accProfitLoss"
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        computeSumForField(initialCurrentYearEquity, "other")
                      )}
                    </TableCell>
                    <TableCell className="text-right border">
                      {/* Compute the total of all fields */}
                      {(() => {
                        const total = [
                          "registeredCapital",
                          "storage",
                          "expansion",
                          "accProfitLoss",
                          "other",
                        ].reduce(
                          (acc, field) =>
                            acc +
                            parseParenthesisNumber(
                              initialCurrentYearEquity?.reduce(
                                (sum, row) =>
                                  sum + parseParenthesisNumber(row[field]),
                                0
                              )
                            ),
                          0
                        );
                        return formatNumberWithCommas(total);
                      })()}
                    </TableCell>
                    <TableCell className="border">-</TableCell>
                  </TableRow>

                  {/* isCurrentYear ==turn */}
                  {equitiesData?.equityDetails
                    ?.filter((equity) => equity.isCurrentYear)
                    .map((equity, index) => (
                      <TableRow
                        key={equity.id}
                        className="[&>*]:whitespace-nowrap "
                      >
                        <TableCell className="border">
                          {equity.name || "-"}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.registeredCapital / unit || "-"} */}
                          {formatNumberWithCommas(equity.registeredCapital)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.storage / unit || "-"} */}
                          {formatNumberWithCommas(equity.storage)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.expansion / unit || "-"} */}
                          {formatNumberWithCommas(equity.expansion)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.accProfitLoss)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {equity.accProfitLoss / unit || "-"} */}
                          {formatNumberWithCommas(equity.other)}
                        </TableCell>
                        <TableCell className="text-right border">
                          {/* {(equity.registeredCapital +
														equity.storage +
														equity.expansion +
														equity.accProfitLoss) /
														unit || "-"} */}

                          {formatNumberWithCommas(
                            (equity.registeredCapital +
                              equity.storage +
                              equity.expansion +
                              equity.accProfitLoss) /
                              unit
                          )}
                        </TableCell>
                        <TableCell className="border">
                          {equity.remark || "-"}
                        </TableCell>
                      </TableRow>
                    ))}

                  <TableRow className="bg-[#216C4C]/20">
                    <TableCell className="text-center border">
                      ຍອດຍັງເຫຼືອ 31 ທັນວາ {year}
                    </TableCell>
                    {/* Sum of total registered capital current year and not current year */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.registeredCapital),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum +
                              parseParenthesisNumber(row.registeredCapital),
                            0
                          )
                      )}
                    </TableCell>

                    {/* Sum of total storage current year and not current year */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.storage),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum + parseParenthesisNumber(row.storage),
                            0
                          )
                      )}
                    </TableCell>

                    {/* Sum of total expansion capital current year and not current year */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.expansion),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum + parseParenthesisNumber(row.expansion),
                            0
                          )
                      )}
                    </TableCell>

                    {/* Sum of total accProfitLoss current year and not current year */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.accProfitLoss),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum + parseParenthesisNumber(row.accProfitLoss),
                            0
                          )
                      )}
                    </TableCell>

                    {/* Sum of total other current year and not current year */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) => sum + parseParenthesisNumber(row.other),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum + parseParenthesisNumber(row.other),
                            0
                          )
                      )}
                    </TableCell>

                    {/* Sum of all fields */}
                    <TableCell className="text-right border">
                      {formatNumberWithCommas(
                        initialNotCurrentYearEquity?.reduce(
                          (sum, row) =>
                            sum +
                            parseParenthesisNumber(row.registeredCapital) +
                            parseParenthesisNumber(row.storage) +
                            parseParenthesisNumber(row.expansion) +
                            parseParenthesisNumber(row.accProfitLoss) +
                            parseParenthesisNumber(row.other),
                          0
                        ) +
                          initialCurrentYearEquity?.reduce(
                            (sum, row) =>
                              sum +
                              parseParenthesisNumber(row.registeredCapital) +
                              parseParenthesisNumber(row.storage) +
                              parseParenthesisNumber(row.expansion) +
                              parseParenthesisNumber(row.accProfitLoss) +
                              parseParenthesisNumber(row.other),
                            0
                          )
                      )}
                    </TableCell>

                    <TableCell className="border">-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {equitiesData?.docFile
              ? company_financial_report?.isDownload && (
                  <FileHandleViewer fileUrl={equitiesData?.docFile} />
                )
              : null}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
